'use client'

import { useCallback, useEffect, useState } from 'react'
import { attemptRefreshingSession } from 'supertokens-auth-react/recipe/session'
import { preloadLogos } from '../shell/logoCache'
import type { Catalog, TenantItem, EmailTypeItem, SourceItem } from './entryTypes'

export type ApiResult<T = any> = { success: boolean; data?: T; error?: string }

const BASE = '/api/entries'

// The access token backing a dashboard's session-only fetches (credentials:
// 'include', no other auth) can go stale while a dashboard sits open and
// polling — e.g. autoRefresh.ts's setInterval — with nothing else on the
// page ever prompting SuperTokens to refresh it, since the SDK's own global-
// fetch interceptor only reacts to its own session-expired signal, not to
// this backend's plain {success:false, error:"Unauthorized"} 401/403 body.
// Left alone, every subsequent poll keeps failing the same way until a full
// reload remounts <SessionAuth> and forces a fresh session check. Retrying
// once after a successful refresh lets a dashboard recover on its own.
async function fetchWithAuthRetry(path: string, init: RequestInit, attempted = false): Promise<Response> {
  const res = await fetch(`${BASE}${path}`, { ...init, credentials: 'include' })
  if (!attempted && (res.status === 401 || res.status === 403)) {
    const refreshed = await attemptRefreshingSession()
    if (refreshed) return fetchWithAuthRetry(path, init, true)
  }
  return res
}

// The backend's own RequestResult types `error` as `any` and passes Supabase/
// Postgrest errors straight through as objects ({code, details, hint, message})
// rather than always stringifying them server-side — so despite ApiResult's
// promise of `error?: string`, res.json() can hand back an object at runtime.
// Rendered directly (e.g. {error} in JSX), that throws "Objects are not valid
// as a React child" instead of showing the actual failure. Normalizing here,
// once, means every apiGet/apiPost/apiPatch caller can trust the type it's
// already relying on instead of each needing its own defensive stringify.
function normalizeApiResult<T>(result: any): ApiResult<T> {
  if (result && typeof result.error === 'object' && result.error !== null) {
    result = { ...result, error: result.error.message ?? JSON.stringify(result.error) }
  }
  return result
}

export async function apiGet<T = any>(path: string): Promise<ApiResult<T>> {
  try {
    const res = await fetchWithAuthRetry(path, {})
    return normalizeApiResult<T>(await res.json())
  } catch (error) {
    return { success: false, error: String(error) }
  }
}

export async function apiPost<T = any>(path: string, body: unknown): Promise<ApiResult<T>> {
  try {
    const res = await fetchWithAuthRetry(path, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    })
    return normalizeApiResult<T>(await res.json())
  } catch (error) {
    return { success: false, error: String(error) }
  }
}

export async function apiPatch<T = any>(path: string, body: unknown): Promise<ApiResult<T>> {
  try {
    const res = await fetchWithAuthRetry(path, {
      method: 'PATCH',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    })
    return normalizeApiResult<T>(await res.json())
  } catch (error) {
    return { success: false, error: String(error) }
  }
}

export async function apiPut<T = any>(path: string, body: unknown): Promise<ApiResult<T>> {
  try {
    const res = await fetchWithAuthRetry(path, {
      method: 'PUT',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    })
    return normalizeApiResult<T>(await res.json())
  } catch (error) {
    return { success: false, error: String(error) }
  }
}

export async function apiDelete<T = any>(path: string): Promise<ApiResult<T>> {
  try {
    const res = await fetchWithAuthRetry(path, { method: 'DELETE' })
    return normalizeApiResult<T>(await res.json())
  } catch (error) {
    return { success: false, error: String(error) }
  }
}

const EMPTY_CATALOG: Catalog = { platforms: [], messageTypes: [], callTypes: [] }

export function useCatalog() {
  const [catalog, setCatalog] = useState<Catalog>(EMPTY_CATALOG)
  const [loading, setLoading] = useState(true)

  const refetch = useCallback(async () => {
    setLoading(true)
    const result = await apiGet<Catalog>('/catalog')
    if (result.success && result.data) {
      setCatalog(result.data)
      // The platforms' logos, once per page load, so every mark (sidebar,
      // tables, the platform dropdown) shows the real logo at once.
      preloadLogos((result.data.platforms ?? []).map((p) => p.logo_url))
    }
    setLoading(false)
  }, [])

  useEffect(() => {
    refetch()
  }, [refetch])

  return { catalog, loading, refetch }
}

export function useTenants() {
  const [tenants, setTenants] = useState<TenantItem[]>([])
  const [loading, setLoading] = useState(true)

  const refetch = useCallback(async () => {
    setLoading(true)
    const result = await apiGet<TenantItem[]>('/tenants')
    if (result.success && result.data) setTenants(result.data)
    setLoading(false)
  }, [])

  useEffect(() => {
    refetch()
  }, [refetch])

  return { tenants, loading, refetch }
}

export function useEmailTypes() {
  const [emailTypes, setEmailTypes] = useState<EmailTypeItem[]>([])
  const [loading, setLoading] = useState(true)

  const refetch = useCallback(async () => {
    setLoading(true)
    const result = await apiGet<EmailTypeItem[]>('/email-types')
    if (result.success && result.data) setEmailTypes(result.data)
    setLoading(false)
  }, [])

  useEffect(() => {
    refetch()
  }, [refetch])

  return { emailTypes, loading, refetch }
}

export function useSources() {
  const [sources, setSources] = useState<SourceItem[]>([])
  const [loading, setLoading] = useState(true)

  const refetch = useCallback(async () => {
    setLoading(true)
    const result = await apiGet<SourceItem[]>('/sources')
    if (result.success && result.data) setSources(result.data)
    setLoading(false)
  }, [])

  useEffect(() => {
    refetch()
  }, [refetch])

  return { sources, loading, refetch }
}

// Distinct free-text `source` strings recently written to user_entries
// (backend /entrysources, via /api/entries/entry-sources) — the ingestion-
// channel string SourceWidget edits and the Unified Entries "Source" column
// shows. Not the sources catalog useSources() above returns (that one is
// source_id / tenant scoping, a separate namespace).
export function useEntrySources() {
  const [entrySources, setEntrySources] = useState<string[]>([])
  const [loading, setLoading] = useState(true)

  const refetch = useCallback(async () => {
    setLoading(true)
    const result = await apiGet<string[]>('/entry-sources')
    if (result.success && Array.isArray(result.data)) setEntrySources(result.data)
    setLoading(false)
  }, [])

  useEffect(() => {
    refetch()
  }, [refetch])

  return { entrySources, loading, refetch }
}
